import React, { useEffect, useState } from "react";
import { Link, useLoaderData, useOutletContext } from "react-router";
import OperationalData from "../features/energy/components/OperationalData";
import { TimePeriod } from "../features/energy/types";
import { Alarm, Site, SiteWithGenerationSummary } from "../types";
import { DashboardOutletContext } from "./Dashboard";
import { isUnderConstruction, siteToMarker } from "../utils";
import { ArrowLeftCircleIcon  } from "@heroicons/react/16/solid";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { getProject } from "../features/energy/api";

interface SitePageLoaderData {
    siteWithGenerationSummary: SiteWithGenerationSummary;
}

const SiteHeader = ({ site }: { site: Site }) => (
    <header>
        <div>
            <h1 className="text-3xl font-semibold text-pacificblue-900">{site.name}</h1>
            <h3 className="text-lg font-bold text-pacificblue-700">{site.address}</h3>
        </div>
        <Link to={`/dashboard/project/${site.project.id}`} className="mt-1 text-sm inline-flex items-center text-gray-500 hover:underline">
            <ArrowLeftCircleIcon className='w-4 h-4 me-1' />{site.project.name}
        </Link>
    </header>
);

const ProjectDetails = () => (
    <div className="row-span-2 col-span-1 bg-white rounded-lg shadow p-4">
        <h2 className="font-semibold">Project Details</h2>
        <ul className="text-gray-700">
            <li>Location: Canberra</li>
            <li>Project Cost: [Placeholder]</li>
            <li>Contractor: [Placeholder]</li>
            <li>Contacts: [Placeholder]</li>
        </ul>
    </div>
);

const SiteInfo = () => {
    return (
        <div>
            <p className="text-xs text-gray-700">
                Brief description of the site.
            </p>
        </div>
    );
};

const InstallationDetails = ({ site }: { site: Site }) => {
    const getStatusClass = (status: string) => {
        switch (status) {
            case 'normal':
                return 'bg-success/20 text-success';
            case 'system-fault':
                return 'bg-error/20 text-error';
            case 'no-data':
                return 'bg-gray-100 text-gray-600';
            case 'manual':
                return 'bg-info/20 text-info';
            case 'under-construction':
                return 'bg-gray-100 text-gray-600';
            default:
                return 'bg-gray-100 text-gray-600';
        }
    };
    const getStatusLabel = (status: string) => {
        if (status === 'under-construction') return 'Under construction';
        if (status === 'system-fault') return 'System fault';
        if (status === 'no-data') return 'No data';
        return status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown';
    };

    return (
        <div>
            <h2 className="text-xl font-bold text-pacificblue-950 mb-2">Installation Details</h2>
            <div className="grid grid-cols-3 gap-4">
                {
                    ['Solar PV', 'BESS'].map((name) => (
                        <div key={name} className="bg-pacificblue-700 rounded-lg text-white p-4">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="font-semibold">{name}</h3>
                                <span className={`ms-2 px-2 py-1 rounded-lg text-xs font-medium ${getStatusClass(site.health_status)}`}>
                                    {getStatusLabel(site.health_status)}
                                </span>
                            </div>
                            <ul className="text-sm">
                                <li><span className="font-bold">Installed Capacity </span>{
                                    name === 'Solar PV'
                                        ? (site.generation_kw || '-') + ' kW'
                                        : (site.storage_capacity_kwh || '-') + ' kWh'
                                }</li>
                                <li><span className="font-bold">Manufacturer </span>{site.manufacturer ? site.manufacturer : '-'}</li>
                                <li><span className="font-bold">Model </span>{'-'}</li>
                            </ul>
                        </div>
                    ))
                }
            </div>
        </div>
    )
};

const AlarmsSection = ({ alarms }: { alarms: Alarm[] }) => {
    return (
        <div>
            <h2 className="text-xl font-bold text-pacificblue-950 mb-2">Recent Alarms</h2>
            <div className="rounded-lg shadow overflow-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-stone-200">
                        <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Started At</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cleared At</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Device</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                        </tr>
                    </thead>
                    <tbody className="bg-stone-100 divide-y divide-gray-200">
                        {alarms.length
                            ? alarms.map((alarm) => (
                                <tr key={alarm.id}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{alarm.started_at}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{
                                        alarm.active ? <span className="flex items-center text-error"><ExclamationTriangleIcon className="h-4 w-4 mr-1" />Active</span> : alarm.cleared_at
                                    }</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{alarm.device}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{alarm.description}</td>
                                </tr>
                            ))
                            : <tr>
                                <td colSpan={4} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">No recent events</td>
                            </tr>}
                    </tbody>
                </table>
            </div>
        </div>
    );
};


const SitePage: React.FC = () => {
    const { siteWithGenerationSummary } = useLoaderData() as SitePageLoaderData;
    const { setMarkers, statusFilter, setCurrentSiteId, setNavigationMarkers } = useOutletContext<DashboardOutletContext>();
    const [projectSites, setProjectSites] = useState<Site[] | null>(null);

    useEffect(() => {
        setCurrentSiteId(siteWithGenerationSummary.instance.id);
    }, [setCurrentSiteId, siteWithGenerationSummary.instance.id]);

    useEffect(() => {
        const loadProjectSites = async () => {
            const existingSites = siteWithGenerationSummary.instance.project.sites;
            if (existingSites && existingSites.length) {
                setProjectSites(existingSites);
                return;
            }

            try {
                const project = await getProject(siteWithGenerationSummary.instance.project.id.toString());
                setProjectSites(project.sites ?? []);
            } catch (error) {
                console.error('Failed to load project sites for navigation', error);
                setProjectSites([]);
            }
        };

        loadProjectSites();
    }, [siteWithGenerationSummary.instance.project.id, siteWithGenerationSummary.instance.project.sites]);

    useEffect(() => {
        setMarkers([siteToMarker(siteWithGenerationSummary.instance)]);
    }, [setMarkers, siteWithGenerationSummary.instance]);

    useEffect(() => {
        const sites = projectSites ?? [siteWithGenerationSummary.instance];
        const filteredSites = sites.filter(site => {
            const hasCoordinates = site.latitude && site.longitude;
            if (!hasCoordinates) {
                return statusFilter['not-on-map'];
            }
            const status = site.health_status as keyof typeof statusFilter;
            if (status in statusFilter) {
                return statusFilter[status];
            }
            return true;
        });
        setNavigationMarkers(filteredSites.map(site => siteToMarker(site)));
    }, [projectSites, setNavigationMarkers, statusFilter, siteWithGenerationSummary.instance]);

    if (!siteWithGenerationSummary || !siteWithGenerationSummary.instance) {
        return <div>Site not found</div>;
    }

    const isManual = siteWithGenerationSummary.instance.health_status === 'manual';

    return (
        <div className="px-4">{
            siteWithGenerationSummary && siteWithGenerationSummary.instance && (
                <div className="grid grid-cols-1 gap-4">
                    {<SiteHeader site={siteWithGenerationSummary.instance} />}
                    {isManual ? (
                        <div className="flex items-center justify-center min-h-[400px]">
                            <p className="text-gray-500 text-lg">There is no remote monitoring capability for this site.</p>
                        </div>
                    ) : (
                        <>
                            {<InstallationDetails site={siteWithGenerationSummary.instance} />}
                            {<OperationalData instanceWithGenerationSummary={siteWithGenerationSummary} initPeriod={TimePeriod.Week} />}
                            {<AlarmsSection alarms={siteWithGenerationSummary.alarms} />}
                        </>
                    )}
                </div>
            )
        }</div>
    );
};

export default SitePage;
